import axios from 'axios';
import { get, sortBy, keyBy, map, uniq, lowerCase } from 'lodash';
import { datadogRum } from '@datadog/browser-rum';
import featureFlags from '@enums/feature-flags';
import { getInitialTenantFromQuery } from '@/js/axios';
import initialiseDatadog from '@/js/utils/initialise-datadog';
import { isEmbedded } from '../../webshell-embed/embedded';
import { shellBridge } from '../../webshell-embed/platform-bridge';

const { canAccessAllCategories, canAccessAllStoreGroups } = featureFlags;

const getInitialContextState = () => ({
    loginMethod: null, // primary authMethod from auth.js config file
    loginRedirectUrl: null,
    oktaClientId: null,
    versionTag: null,
    env: null,
    subBrand: null,
    datadog: {},
    clientUrl: null,
    isMaintenanceModeEnabled: false,
});

const getInitialUserContextState = () => ({
    csrfToken: null,
    profile: null,
    loading: false,
    currentLoginMethod: null, // authMethod used on session scope, needed for correct logout
    expireAt: null,
    availableTenants: [],
    currentTenant: null,
});

const store = {
    namespaced: true,

    state: { ...getInitialContextState(), ...getInitialUserContextState() },

    getters: {
        userUnits: (state, getters, rootState, rootGetters) => {
            if (rootState.clientConfig.toggleLogic[canAccessAllCategories]) {
                return sortBy(rootGetters['hierarchy/getAllUnits'], [
                    i => lowerCase(i.levelEntryDescription),
                ]);
            }

            if (state.profile) {
                const categories = sortBy(
                    state.profile.access.categories.map(levelEntryKey =>
                        rootGetters['hierarchy/getHierarchyByKey'](levelEntryKey)
                    ),
                    [i => lowerCase(i.levelEntryDescription)]
                );

                const units = uniq(map(categories, 'parentId'));

                return sortBy(
                    units.map(levelEntryKey =>
                        rootGetters['hierarchy/getHierarchyByKey'](levelEntryKey)
                    ),
                    [i => lowerCase(i.levelEntryDescription)]
                );
            }

            return [];
        },
        userCategories: (state, getters, rootState, rootGetters) => {
            if (rootState.clientConfig.toggleLogic[canAccessAllCategories]) {
                return sortBy(rootGetters['hierarchy/getAllCategories'], [
                    i => lowerCase(i.levelEntryDescription),
                ]);
            }
            return state.profile
                ? sortBy(
                      get(state, 'profile.access.categories', [])
                          .map(levelEntryKey =>
                              rootGetters['hierarchy/getHierarchyByKey'](levelEntryKey)
                          )
                          .filter(cat => cat), // Sometimes users might have categories assigned that are not actually categories. Can be seen in test users on different envs
                      [i => lowerCase(i.levelEntryDescription)]
                  )
                : [];
        },
        userAcccess: state => key => get(state, `profile.access.${key}`),
        userUnitKeys: (state, getter) => map(getter.userUnits, 'levelEntryKey'),
        userCategoryKeys: (state, getter) => map(getter.userCategories, 'levelEntryKey'),
        userCategoryValues: state => get(state, 'profile.access.categories'),
        userRDWCategoryKeys: (state, getter) => map(getter.userCategories, 'rdwHierarchyKey'),
        userCategoriesDescription: (state, getter) => getter.userAcccess('categories'),
        userBrandsDescription: (state, getter) => getter.userAcccess('brands'),
        userChannelsDescription: (state, getter) => getter.userAcccess('channels'),
        userCategoriesKeyMap: (state, getters) => {
            return keyBy(getters.userCategories, 'levelEntryKey');
        },
        userId: state => state.profile && state.profile._id,
        userRoles: state => state.profile && state.profile.roles,
        hasRole: state => role => get(state, 'profile.roles', []).includes(role),
        userPermissions: state => new Set(get(state, 'profile.permissions')),
        hasPermission: (state, getters) => permission => getters.userPermissions.has(permission),
        userStoreGroups: (state, getters, rootState, rootGetters) => {
            if (rootState.clientConfig.toggleLogic[canAccessAllStoreGroups]) {
                return rootGetters['storeGroups/getStoreGroupsOptions'];
            }
            // Resolve access keys against the store groups loaded for the current tenant.
            if (state.profile && state.profile.access && state.profile.access.storeGroups) {
                return state.profile.access.storeGroups.reduce((result, key) => {
                    const sg = rootGetters['storeGroups/storeGroupsMapByKey'][key];
                    if (sg) {
                        result.push(sg);
                    }
                    return result;
                }, []);
            }
            return [];
        },
        userStoreGroupsMap: (state, getters) => {
            return keyBy(getters.userStoreGroups, 'key');
        },
        getCurrentTenant: state => state.currentTenant,
        getAvailableTenants: state => state.availableTenants || [],
        isMultitenancyEnabled: state => state.availableTenants && state.availableTenants.length > 0,
    },

    mutations: {
        setContext(state, contextData) {
            Object.assign(state, contextData);
        },
        setUserContext(state, profile) {
            state.profile = profile;
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setLoginMethod(state, method) {
            state.loginMethod = method;
        },
        setCurrentLoginMethod(state, method) {
            state.currentLoginMethod = method;
        },
        setExpireAt(state, expireAt) {
            state.expireAt = expireAt;
        },
        setCsrfToken(state, token) {
            state.csrfToken = token;
        },
        resetUserContextState(state) {
            Object.assign(state, { ...state, ...getInitialUserContextState() });
        },
        setAvailableTenants(state, tenants) {
            state.availableTenants = tenants;
        },
        setCurrentTenant(state, tenant) {
            state.currentTenant = tenant;
        },
    },

    actions: {
        loadContext({ commit }) {
            return axios.get('/api/context').then(res => commit('setContext', res.data));
        },
        loginHardcoded({ dispatch, commit }, credentials) {
            return axios.post('/api/login', credentials).then(
                async () => {
                    commit('setCurrentLoginMethod', 'ow-auth-hardcoded');
                    await dispatch('loadContext');
                    return dispatch('loadUserContext');
                },
                err => {
                    console.error(err.response.data.message);
                    commit('setCurrentLoginMethod', 'ow-auth-hardcoded');
                }
            );
        },

        loginOkta({ dispatch, commit }, token) {
            return axios.post('/api/ow-auth/login', { token }).then(
                async () => {
                    commit('setCurrentLoginMethod', 'ow-auth-okta');
                    await dispatch('loadContext');
                    return dispatch('loadUserContext');
                },
                err => {
                    console.error(err.response.data.message);
                    commit('setCurrentLoginMethod', 'ow-auth-okta');
                }
            );
        },

        setCurrentLoginMethod({ commit }, method) {
            commit('setCurrentLoginMethod', method);
        },

        initialiseTenantFromQuery({ commit }) {
            const tenantKey = getInitialTenantFromQuery();
            if (tenantKey) commit('setCurrentTenant', tenantKey);
        },

        async updateUserContext({ commit }) {
            // Runs inside a refresh, so a 401 here must not wait on that same refresh.
            const { data } = await axios.get('/api/user-context', { _triedRefreshToken: true });
            if (!data) return;

            // save tokenLifeTime in milliseconds if valid login exists
            const tokenLifetime = (data.exp - data.iat) * 1000;
            const expirationDate = Date.now() + tokenLifetime;
            commit('setUserContext', data.profile);
            commit('setCsrfToken', data.csrfToken);
            commit('setExpireAt', expirationDate);
        },

        async loadUserContext({ state, dispatch, commit, rootState }) {
            const { data } = await axios.get('/api/user-context');
            if (!data) return;

            // The shell owns RUM for the page when embedded.
            if (!isEmbedded()) initialiseDatadog(state);

            datadogRum.setUser({
                id: data.profile._id,
                name: data.profile.username,
                hostname: state.clientUrl,
            });

            datadogRum.startSessionReplayRecording();

            // save tokenLifeTime in milliseconds if valid login exists
            const tokenLifetime = (data.exp - data.iat) * 1000;
            const expirationDate = Date.now() + tokenLifetime;
            commit('setUserContext', data.profile);
            commit('setCsrfToken', data.csrfToken);
            commit('setExpireAt', expirationDate);

            // The server response is authoritative after validating the bootstrap tenant.
            const availableTenants = data.tenants || [];
            commit('setAvailableTenants', availableTenants);
            await dispatch('setCurrentTenant', data.currentTenant);

            // this is where we know the user has successfully authenticated so we can load state
            await dispatch('initialiseStateBlocking', null, { root: true });
            if (!get(rootState, 'clientConfig.toggleLogic.reportingOnly', false)) {
                dispatch('initialiseStateBackground', null, { root: true });
            }
        },

        refreshUserContext({ dispatch }) {
            // Embedded, the shell holds the refresh token and promo cannot post to the shell's
            // refresh endpoint cross-origin, so the refresh is brokered through the bridge and the
            // new JWT arrives as a cookie - ADR-003 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/003-centralize-auth-in-platform-auth-service.md
            const refresh = isEmbedded()
                ? shellBridge()?.refreshJWT()
                : axios.post('/api/token/refresh');

            if (!refresh) return Promise.reject(new Error('no token refresh available'));

            return Promise.resolve(refresh).then(() => dispatch('updateUserContext'));
        },

        logout({ state, commit }) {
            if (isEmbedded()) throw new Error('the shell owns logout while embedded');

            // currentLoginMethod is only set right before a full-page redirect to the IdP
            // (see login-openid.vue/login-saml.vue), so it is lost on reload for SSO flows.
            // Fall back to loginMethod, which is the server's configured authMethod and always
            // available via /api/context, to reliably pick the correct logout endpoint.
            const effectiveLoginMethod = state.currentLoginMethod || state.loginMethod;
            let logoutUrl = '/api/logout';
            if (effectiveLoginMethod === 'ow-auth-okta') logoutUrl = '/api/ow-auth/logout';
            if (effectiveLoginMethod === 'auth-saml') logoutUrl = '/api/auth-saml/logout';
            if (effectiveLoginMethod === 'auth-openid') logoutUrl = '/api/auth-openid/logout';

            return axios.get(logoutUrl).then(res => {
                commit('setUserContext', null);
                commit('setCsrfToken', null);
                datadogRum.removeUser();
                datadogRum.stopSessionReplayRecording();

                if (res.data && res.data.redirectUrl) {
                    // Some of our auth strategies, like SAML and OpenID, are implemented
                    // so the user can close their session within the auth provider, which will then
                    // redirect them back to our home page.
                    window.location.href = res.data.redirectUrl;
                    return Promise.reject();
                }
                return Promise.resolve(
                    effectiveLoginMethod === 'ow-auth-okta' ? '/ow-auth/login' : '/login'
                );
            });
        },

        resetState({ commit }) {
            commit('resetUserContextState');
        },
        setAvailableTenants({ commit }, availableTenants) {
            commit('setAvailableTenants', availableTenants);
        },
        setCurrentTenant({ commit, state }, tenantKey) {
            if (isEmbedded()) {
                // When embedded, the webshell manages the current tenant, and it is provided
                // in the URL during promo app initialization.
                // When changing tenant, the webshell will reload the page
                commit('setCurrentTenant', tenantKey);
                return;
            }
            // If no tenantKey provided or tenantKey is not in available list, set to first available
            if (!tenantKey || !state.availableTenants?.some(tenant => tenant.key === tenantKey)) {
                if (state.availableTenants?.length) {
                    commit('setCurrentTenant', state.availableTenants[0].key);
                }
                return;
            }

            const STORAGE_KEY = 'store';
            // Persist tenant selection in localStorage so it can be restored on next login or tab open.
            try {
                window.localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({ currentTenant: tenantKey })
                );
            } catch (e) {
                // Silently handle localStorage write failures (e.g. quota exceeded).
            }

            commit('setCurrentTenant', tenantKey);
        },
    },
};

export default store;
